import { useEffect } from 'react';
import Footer from '../../components/feature/Footer';

export default function TermsAndConditionsPage() {
  useEffect(() => {
    updatePageSEO({
      title: 'Terms and Conditions | Curtis Waters AI Solutions',
      description: 'Terms and conditions for Curtis Waters AI Solutions services. Review our service agreement, user responsibilities, intellectual property rights, and legal terms for AI-powered deal evaluation and business intelligence.',
      keywords: 'terms of service, legal agreement, service terms, AI solutions terms, business intelligence agreement, deal evaluation terms, user agreement, service conditions',
      canonical: `${import.meta.env.VITE_SITE_URL}/terms-and-conditions`,
      ogTitle: 'Terms and Conditions | Curtis Waters AI Solutions',
      ogDescription: 'Terms and conditions for Curtis Waters AI Solutions services. Review our service agreement, user responsibilities, intellectual property rights, and legal terms for AI-powered deal evaluation and business intelligence.',
      ogUrl: `${import.meta.env.VITE_SITE_URL}/terms-and-conditions`,
    });
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://curtiswaters.ai';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms and Conditions - Curtis Waters AI",
      "description": "Terms and Conditions for Curtis Waters AI services. Read our terms of use and service agreement.",
      "url": `${siteUrl}/terms-and-conditions`
    };

    const existingScript = document.querySelector('script[data-schema="terms"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'terms');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="terms"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gray-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/" className="inline-flex items-center text-gray-300 hover:text-white mb-4 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </a>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
            Terms and Conditions
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you and Curtis Waters AI ("Company," "we," "us," or "our") concerning your access to and use of the curtiswaters.ai website and our AI-powered business intelligence services (collectively, the "Services").
            </p>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree with these Terms, you must not access or use our Services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use of Services</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.1 Eligibility</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You must be at least 18 years old and have the legal capacity to enter into these Terms. By using our Services, you represent and warrant that you meet these requirements.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 License to Use</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Services for your personal or business purposes.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.3 Prohibited Uses</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Use the Services for any illegal purpose or in violation of any laws</li>
              <li>Attempt to gain unauthorized access to our systems or networks</li>
              <li>Interfere with or disrupt the Services or servers</li>
              <li>Use automated systems to access the Services without permission</li>
              <li>Copy, modify, distribute, or create derivative works from our content</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Use the Services to transmit viruses, malware, or harmful code</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Collect or harvest information about other users</li>
              <li>Use the Services for competitive purposes or to develop competing products</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Our Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              All content, features, and functionality of the Services, including but not limited to text, graphics, logos, images, software, and AI algorithms, are owned by Curtis Waters AI or our licensors and are protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Your Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You retain ownership of any content you submit to our Services. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content solely for the purpose of providing and improving our Services.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Trademarks</h3>
            <p className="text-gray-700 leading-relaxed">
              "Curtis Waters AI" and related logos are trademarks of Curtis Waters AI. You may not use these trademarks without our prior written permission.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. AI Services and Accuracy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our AI-powered services provide business intelligence, deal evaluation, and people assessment tools. While we strive for accuracy, you acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>AI-generated insights are based on algorithms and data analysis</li>
              <li>Results may not be 100% accurate and should be used as guidance, not absolute truth</li>
              <li>You are responsible for verifying information and making your own business decisions</li>
              <li>We do not guarantee specific outcomes or results from using our Services</li>
              <li>AI recommendations should be combined with human judgment and expertise</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Accounts</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you create an account with us, you are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Providing accurate and complete information</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent or illegal activities.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Payment and Fees</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you purchase services from us:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>You agree to pay all fees as described at the time of purchase</li>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>We reserve the right to change our pricing with notice</li>
              <li>You are responsible for all taxes associated with your purchase</li>
              <li>Payment information must be accurate and current</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
            <p className="text-gray-700 leading-relaxed">
              Your use of our Services is also governed by our <a href="/privacy-policy" className="text-red-600 hover:text-red-700 underline cursor-pointer">Privacy Policy</a>. By using our Services, you consent to the collection and use of your information as described in our Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimers and Limitations of Liability</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.1 Disclaimer of Warranties</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not warrant that:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>The Services will be uninterrupted, secure, or error-free</li>
              <li>The results obtained from the Services will be accurate or reliable</li>
              <li>Any errors in the Services will be corrected</li>
              <li>The Services will meet your specific requirements</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">8.2 Limitation of Liability</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CURTIS WATERS AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-gray-700 leading-relaxed">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR $100, WHICHEVER IS GREATER.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Curtis Waters AI, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with your access to or use of the Services, your violation of these Terms, or your violation of any rights of another party.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Services may contain links to third-party websites or services that are not owned or controlled by Curtis Waters AI. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that we shall not be liable for any damage or loss caused by your use of any third-party services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may terminate or suspend your access to our Services immediately, without prior notice or liability, for any reason, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Breach of these Terms</li>
              <li>Fraudulent or illegal activities</li>
              <li>Request by law enforcement or government agencies</li>
              <li>Discontinuation or material modification of the Services</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Upon termination, your right to use the Services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law and Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">12.1 Governing Law</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the United States and the State of North Carolina, without regard to its conflict of law provisions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">12.2 Dispute Resolution</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any dispute arising out of or relating to these Terms or the Services shall be resolved through:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2">
              <li>Informal negotiation between the parties</li>
              <li>If unresolved within 30 days, binding arbitration in accordance with the rules of the American Arbitration Association</li>
              <li>Arbitration shall take place in North Carolina</li>
              <li>Each party shall bear its own costs and fees</li>
            </ol>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Services after any changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Entire Agreement</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms, together with our Privacy Policy and any other legal notices published by us on the Services, constitute the entire agreement between you and Curtis Waters AI concerning the Services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Curtis Waters AI</strong></p>
              <p className="text-gray-700 mb-2">Email: <a href="mailto:curtis@curtiswaters.com" className="text-red-600 hover:text-red-700 underline cursor-pointer">curtis@curtiswaters.com</a></p>
              <p className="text-gray-700">Website: <a href="https://curtiswaters.ai" className="text-red-600 hover:text-red-700 underline cursor-pointer">curtiswaters.ai</a></p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}